import React from 'react';
import { PortableText } from '../PortableText';
import { SanityObject } from '../../../lib/landingPage';

// eslint-disable-next-line
type WrappedProps<P extends Record<string, unknown> = any> = {
    value: SanityObject<P> | SanityObject<P>[];
} & Partial<P>;

// eslint-disable-next-line
type WrappedComponent<P extends Record<string, unknown> = any> =
    React.FunctionComponent<WrappedProps<P>> & {
        Component: React.FunctionComponent<P>;
        WrappedComponent: React.FunctionComponent<{ value: SanityObject<P> }>;
        type: string;
    };

function mergeAdditionalProps<P extends Record<string, unknown>>({
    value,
    ...rest
}: WrappedProps<P>) {
    return Array.isArray(value)
        ? value.map(v => ({ ...v, ...rest }))
        : { ...value, ...rest };
}

function Wrapper<P extends Record<string, unknown>>(
    Component: React.FunctionComponent<P>,
    type: string
): WrappedComponent<P> {
    function WrappedComponent({ value, ...rest }: { value: SanityObject<P> }) {
        return <Component {...value} {...rest} />;
    }

    function Wrapped(props: WrappedProps<P>) {
        return (
            <PortableText
                components={{
                    types: {
                        [type]: WrappedComponent,
                    },
                }}
                value={mergeAdditionalProps(props)}
            />
        );
    }

    Wrapped.Component = Component;
    Wrapped.WrappedComponent = WrappedComponent;
    Wrapped.type = type;
    return Wrapped;
}

function Union({
    components,
    value,
    ...rest
}: WrappedProps & { components: WrappedComponent[] }): JSX.Element {
    return (
        <PortableText
            value={mergeAdditionalProps({ value, ...rest })}
            components={{
                types: (components as WrappedComponent[]).reduce(
                    (obj, component) => ({
                        ...obj,
                        [component.type]: component.WrappedComponent,
                    }),
                    {}
                ),
            }}
        />
    );
}

export { Wrapper, Union };
